import type { TimeEntryItem } from '@hygge/shared';
import type { EditTargetChanges } from './edit-entry-target';
import type { EditPayload } from './linked-time-fields';

/**
 * Whether saving the Edit dialog as it stands would send the row back
 * unchanged.
 *
 * Judged on the PAYLOAD a save would send (`editSubmission`), not on the
 * boxes: the payload is what the server compares against the stored row
 * (`patchChangesNothing`), so every field that payload carries is compared
 * here the way the server compares it — a Duration retyped to the value it
 * had, a date moved and moved back, both come out as the stored figures and
 * leave Save off. The server answers such a request with a 200
 * no-op anyway (HW-973); this keeps the dialog from offering a button that
 * does nothing.
 *
 * Instants are compared as instants, since the stored `startedAt` and a
 * pass-through payload may spell the same moment differently; the note is
 * compared as the dialog sends it (`description || null`), so an emptied box
 * against a stored null or a stored '' is no change — both are "no note", and
 * `patchChangesNothing` reads them the same way. Null payload means "may not be sent",
 * which `canSave` already answers — not "unchanged".
 *
 * The end is compared too, against the payload's derived one: the server
 * stores start + duration on every save that sends the trio, so a row stored
 * with no end, or one off that sum, is repaired by a plain Save and is a
 * change there (`patchChangesNothing`). Ignoring it here would leave such a
 * row with no button that fixes it. [HW-887]
 *
 * The zone is compared too: a legacy row (stored `timezone` null) whose start
 * is rebuilt is sent with the viewer's zone (`editEntryTimes`), and the server
 * stores it — so that save is a change even at the stored instants, and so is
 * a zone other than the stored one. A zoned row is sent with its own zone, and
 * a pass-through sends none; neither is a change.
 *
 * The project, the task and Billable Hours ride along as `changes` — only the
 * fields a save would send (`editTargetChanges`) — so a project-, task- or
 * billable-only edit enables Save like any other. [HW-974]
 *
 * A `.ts` helper rather than a line in the dialog because this repo's vitest
 * is node-env and cannot mount a component. [HW-973]
 */
export function editChangesNothing(
  stored: Pick<TimeEntryItem, 'startedAt' | 'endedAt' | 'duration' | 'description' | 'timezone'>,
  payload: EditPayload | null,
  description: string,
  changes: EditTargetChanges = {}
): boolean {
  if (!payload) return false;
  if (Object.keys(changes).length > 0) return false;
  if (payload.duration !== stored.duration) return false;
  if (new Date(payload.startedAt).getTime() !== new Date(stored.startedAt).getTime()) return false;
  if (stored.endedAt == null) return false;
  if (new Date(payload.endedAt).getTime() !== new Date(stored.endedAt).getTime()) return false;
  if (payload.timezone && payload.timezone !== stored.timezone) return false;
  return (description || null) === (stored.description || null);
}
